import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Switch,
    TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'

export enum FormValueType {
    TEXT,
    NUMBER,
    PERCENTAGE,
    CURRENCY,
    DATE,
    PICKER,
    CHECKBOX,
    SWITCH,
}

type FormValue = string | number | boolean

export type DataEntry = {
    key: string
    label: string
    type: FormValueType
    initialValue?: FormValue
    readOnly?: boolean
    options?: { [key: string]: any }
    pickerValues?: { label: string | number; value: FormValue }[]
}

export type FormResult = { [key: string]: FormValue }

interface OneStepFormProps {
    formData: DataEntry[]
    validate?: (formData: any[]) => boolean
    submitUntouched?: boolean
    controlled?: boolean
    onChange?: (result: FormResult) => void
    onSubmit?: (result: FormResult) => void
}

const OneStepForm = (props: OneStepFormProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { formData, validate, submitUntouched, controlled, onSubmit, onChange } = props
    const [result, setResult] = useState<FormResult>({})

    const handleChange = (entry: DataEntry, value: any) => {
        const update = { ...result }
        switch (entry.type) {
            case FormValueType.NUMBER:
            case FormValueType.CURRENCY:
            case FormValueType.PERCENTAGE: {
                const parsedValue = parseFloat(value)
                if (parsedValue === entry.initialValue) {
                    delete update[entry.key]
                } else if (!Number.isNaN(parsedValue)) {
                    update[entry.key] = parsedValue
                }
                break
            }
            default: {
                if (value === entry.initialValue) {
                    delete update[entry.key]
                } else {
                    update[entry.key] = value
                }
            }
        }

        // validate, enable/disable submit button

        setResult(update)
        onChange && onChange(update)
    }

    const handleSubmit = () => {
        // check for changes w/ initial value, filter
        onSubmit && onSubmit(result)
    }

    const renderControl = (entry: DataEntry) => {
        const sx = { width: '100%' }
        const value = result[entry.key] ?? entry.initialValue
        switch (entry.type) {
            case FormValueType.TEXT:
            case FormValueType.NUMBER:
            case FormValueType.PICKER:
                return (
                    <TextField
                        sx={sx}
                        value={value}
                        disabled={entry.readOnly}
                        onChange={(e) => handleChange(entry, e.target.value)}
                        label={entry.label}
                        select={entry.type === FormValueType.PICKER}
                        // TODO: Better number validation
                        type={entry.type === FormValueType.NUMBER ? 'number' : 'text'}
                    >
                        {entry.type === FormValueType.PICKER &&
                            entry.pickerValues?.map((item, i) => (
                                <MenuItem key={i} value={item.value as string | number}>
                                    {item.label}
                                </MenuItem>
                            ))}
                    </TextField>
                )
            case FormValueType.CURRENCY:
                return (
                    <NumericFormat
                        sx={sx}
                        disabled={entry.readOnly}
                        customInput={TextField}
                        prefix="$"
                        type="text"
                        label={entry.label}
                        value={value as number}
                        onValueChange={(e: any) => handleChange(entry, e.value)}
                    />
                )
            case FormValueType.PERCENTAGE:
                return (
                    <NumericFormat
                        sx={sx}
                        disabled={entry.readOnly}
                        customInput={TextField}
                        suffix="%"
                        decimalScale={2}
                        type="text"
                        label={entry.label}
                        value={(value as number) * 100}
                        onValueChange={(e: any) => handleChange(entry, e.value)}
                    />
                )
            case FormValueType.DATE:
                return (
                    <DatePicker
                        label={entry.label}
                        value={dayjs(value as number)}
                        disabled={entry.readOnly}
                        // TODO
                        onChange={(e: any) =>
                            handleChange(entry, dayjs(e).startOf('day').toDate().getTime())
                        }
                    />
                )
            case FormValueType.CHECKBOX:
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={entry.readOnly}
                                sx={sx}
                                checked={value as boolean}
                                onChange={(e) => handleChange(entry, e.target.checked)}
                            />
                        }
                        label={entry.label}
                    />
                )
            case FormValueType.SWITCH:
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                disabled={entry.readOnly}
                                checked={value as boolean}
                                onChange={(e) => handleChange(entry, e.target.checked)}
                            ></Switch>
                        }
                        label={entry.label}
                        labelPlacement="start"
                    />
                )
        }
    }

    return (
        <>
            <FormControl sx={{ width: '100%' }} component="fieldset" variant="standard">
                <FormGroup
                    sx={{
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                        padding: 1,
                    }}
                >
                    {formData.map((entry) => (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                            key={entry.key}
                        >
                            {renderControl(entry)}
                        </Box>
                    ))}
                </FormGroup>
            </FormControl>
            {!controlled && (
                <Button sx={{ marginLeft: 1 }} onClick={handleSubmit}>
                    Submit
                </Button>
            )}
        </>
    )
}

export default OneStepForm
